import { effectScope } from "./scope";
import { currentPath } from "../stores/route";

export type ViewFactory = (params: Record<string, string>) => Node;
export type GuardFn = () => boolean | string;

interface Route {
  pattern: RegExp;
  keys: string[];
  factory: ViewFactory;
}

export interface Router {
  register(path: string, factory: ViewFactory): Router;
  start(outlet: Element): void;
  navigate(path: string): void;
  /** Arms a guard checked on every navigation attempt until a navigation succeeds (a
   * cancelled confirm leaves it armed — Phase 2a item 11). Pass null to disarm. */
  guard(fn: GuardFn | null): void;
  /** View rendered when no route matches. Registered from main.ts rather than built in
   * here so lib/ stays free of brand and view imports; without one, a bare text node. */
  notFound(factory: ViewFactory): Router;
}

function compile(path: string): { pattern: RegExp; keys: string[] } {
  const keys: string[] = [];
  const source = path.replace(/:[a-zA-Z]+/g, (segment) => {
    keys.push(segment.slice(1));
    return "([^/]+)";
  });
  return { pattern: new RegExp(`^${source}$`), keys };
}

export function createRouter(): Router {
  const routes: Route[] = [];
  let outlet: Element | null = null;
  let currentDispose: (() => void) | null = null;
  let guardFn: GuardFn | null = null;
  let notFoundFactory: ViewFactory | null = null;

  function render(path: string): void {
    if (!outlet) return;
    guardFn = null; // the guarding view is being disposed — its guard goes with it
    currentPath.value = path;
    for (const route of routes) {
      const match = route.pattern.exec(path);
      if (!match) continue;

      const params: Record<string, string> = {};
      route.keys.forEach((key, i) => (params[key] = decodeURIComponent(match[i + 1] ?? "")));

      currentDispose?.();
      outlet.replaceChildren();
      const { node, dispose } = effectScope(() => route.factory(params));
      outlet.appendChild(node);
      currentDispose = dispose;
      return;
    }
    // Dispose here too. The previous view's effects outlive it otherwise — every
    // navigation to a dead URL used to leak one scope, since only the matched branch
    // above ever called this.
    currentDispose?.();
    currentDispose = null;
    outlet.replaceChildren();
    // Copied to a const: the closure below defeats narrowing on the mutable binding.
    const fallback = notFoundFactory;
    if (fallback) {
      const { node, dispose } = effectScope(() => fallback({}));
      outlet.appendChild(node);
      currentDispose = dispose;
    } else {
      outlet.appendChild(document.createTextNode("Not found"));
    }
  }

  function navigate(path: string): void {
    if (guardFn) {
      const result = guardFn();
      if (result !== true) {
        const message = typeof result === "string" ? result : "You have unsaved changes. Leave anyway?";
        // Cancelling keeps the guard armed — the next attempt must re-confirm.
        if (!window.confirm(message)) return;
      }
    }
    if (location.pathname !== path) history.pushState({}, "", path);
    render(path);
  }

  function start(el: Element): void {
    outlet = el;
    window.addEventListener("popstate", () => render(location.pathname));
    document.addEventListener("click", (event) => {
      const anchor = (event.target as Element).closest?.("a[data-link]");
      if (anchor instanceof HTMLAnchorElement && anchor.origin === location.origin) {
        event.preventDefault();
        navigate(anchor.pathname);
      }
    });
    render(location.pathname);
  }

  function guard(fn: GuardFn | null): void {
    guardFn = fn;
  }

  const router: Router = {
    register(path, factory) {
      const { pattern, keys } = compile(path);
      routes.push({ pattern, keys, factory });
      return router;
    },
    start,
    navigate,
    guard,
    notFound(factory) {
      notFoundFactory = factory;
      return router;
    },
  };
  return router;
}
